/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useEffect } from "react";
import { View } from "react-native";
import { HashRouter, Route, Redirect } from "react-router-dom";
import { AnimatedSwitch } from "react-router-transition";
import {
  createTheme as createMuiTheme,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles";
import { Client as Styletron } from "styletron-engine-atomic";
import { Provider as StyletronProvider } from "styletron-react";
import { createTheme, BaseProvider } from "baseui";
import { isMobile, isTablet } from "react-device-detect";
import { GoogleOAuthProvider } from "@react-oauth/google";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import { AnnouncementModal } from "UpduoLibrary";
import { skaleBlue, skaleLinkBlue } from "config/UpduoColors";
import NavigationBar from "components/NavigationBar";
import HomeRoute from "components/HomeRoute";
import SummitRoute from "components/SummitRoute";
import ContentLibraryRoute from "components/ContentLibraryRoute";
import StartRoute from "components/StartRoute";
import JoinOrganizationRoute from "components/JoinOrganizationRoute";
import DashboardRoute from "components/DashboardRoute";
import CurriculumRoute from "components/CurriculumRoute";
import CurriculumDetailRoute from "components/CurriculumDetailRoute";
import SettingsRoute from "components/SettingsRoute";
import SkillModuleDetailRoute from "components/SkillModuleDetailRoute";
import SummitAdminListRoute from "components/SummitAdminListRoute";
import SummitAdminRoute from "components/SummitAdminRoute";
import SummitDetailEditRoute from "components/SummitDetailEditRoute";
import AnnouncementsRoute from "components/AnnouncementsRoute";
import OnboardingRoute from "components/OnboardingRoute";
import PerformanceRoute from "components/PerformanceRoute";
import VideoRoute from "components/VideoRoute";
import { timeResetState, instantiatePrimaryUser } from "config/ResetState";
import { defineApiHeaders } from "config/UpduoService";
import UserDashboardRoute from "components/UserDashboardRoute";
import { SummitType } from "graphql/internal/generated";
import QuotaDashboardRoute from "components/QuotaDashboardRoute";
import KudosModerationRoute from "components/KudosModerationRoute";
import cleanStore from "store/validation";

const engine = new Styletron();

const baseTheme = createTheme({
  primaryFontFamily: "ObjectSans-Regular",
  primary: skaleBlue,
});

const skaleTheme = createMuiTheme({
  palette: {
    primary: {
      main: skaleLinkBlue,
    },
    secondary: {
      main: skaleLinkBlue,
    },
  },
});

const switchConfig = {
  atEnter: { opacity: 0 },
  atLeave: { opacity: 0 },
  atActive: { opacity: 1 },
};

function mapStyles(styles) {
  return { opacity: styles.opacity };
}

function PrivateRoute({ component: Component, ...rest }) {
  // Route component that can only be reached if user authToken exists
  const authToken = useSelector((state: RootState) => state.authToken);
  return (
    <Route
      {...rest}
      render={(props) =>
        authToken ? (
          <Component {...props} />
        ) : (
          <Redirect
            to={{
              pathname: "/",
              state: { referrer: props.location },
            }}
          />
        )
      }
    />
  );
}

export default function App() {
  // The main web app container
  const dispatch = useDispatch();
  const primaryAuthToken = useSelector(
    (state: RootState) => state.primaryAuthToken
  );
  const selfObject = useSelector((state: RootState) => state.selfObject);
  const displayedSummitView = useSelector(
    (state: RootState) => state.displayedSummitView
  );
  const selectedSummit = useSelector(
    (state: RootState) => state.selectedSummit
  );

  window["OneSignal"] = window["OneSignal"] || [];

  useEffect(() => {
    window["OneSignal"].push(() => {
      if (process.env.REACT_APP_CONFIG === "staging") {
        window["OneSignal"].SERVICE_WORKER_PARAM = { scope: "/beta/" };
        window["OneSignal"].SERVICE_WORKER_PATH = "beta/OneSignalSDKWorker.js";
        window["OneSignal"].SERVICE_WORKER_UPDATER_PATH =
          "beta/OneSignalSDKUpdaterWorker.js";
      }
      window["OneSignal"].init({
        appId: process.env.REACT_APP_ONESIGNAL_APP_ID,
        allowLocalhostAsSecureOrigin:
          process.env.REACT_APP_CONFIG?.match(/local|staging/),
        notifyButton: { enable: true },
      });
    });
    dispatch(ActionCreators.setSelectedSummit({} as SummitType));
    dispatch(ActionCreators.setDisplayedSummitView("Summit"));
    defineApiHeaders();
    timeResetState();
    if (primaryAuthToken) {
      instantiatePrimaryUser();
    }
  }, []);

  useEffect(() => {
    if (selectedSummit?.name && selectedSummit?.organization) {
      document.title = `Upduo: ${selectedSummit?.organization?.name} - ${selectedSummit?.name}`;
    } else if (selfObject.organizationName) {
      document.title = `Upduo: ${selfObject.organizationName}`;
    } else {
      document.title = "Upduo";
    }
  }, [selectedSummit, selfObject.organizationName]);

  useEffect(() => {
    cleanStore();
  }, []);

  if (isMobile || isTablet) {
    return (
      <HashRouter>
        <View>
          <Route
            path={"/"}
            component={() => {
              window.location.href = "http://www.upduo.com/mobile/";
              return null;
            }}
          />
        </View>
      </HashRouter>
    );
  }

  // Hack for "library" organizations:
  // Only Content page for non-Maintainance users, plus Members page and editing only for Maintenance users:
  const libraryOrganizationIds =
    process.env.REACT_APP_LIBRARY_ORGANIZATION_IDS?.split(",") || [];
  const isLibraryOrganization = libraryOrganizationIds.includes(
    selfObject?.organizationId
  );
  if (isLibraryOrganization) {
    return (
      <GoogleOAuthProvider
        clientId={
          process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID
            ? process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID
            : ""
        }
      >
        <StyletronProvider value={engine}>
          <BaseProvider theme={baseTheme}>
            <MuiThemeProvider theme={skaleTheme}>
              <HashRouter>
                {primaryAuthToken &&
                !window.location.href.includes("library") ? (
                  <NavigationBar />
                ) : null}
                <AnimatedSwitch {...switchConfig} mapStyles={mapStyles}>
                  <Route exact path={"/"} component={CurriculumRoute} />
                  <Route
                    exact
                    path={"/library/:sublibrary?"}
                    component={ContentLibraryRoute}
                  />
                  <PrivateRoute
                    exact
                    path={"/content"}
                    component={CurriculumRoute}
                  />
                  <PrivateRoute
                    exact
                    path={"/content/topic/:idHash/:cloneIdHash?"}
                    component={SkillModuleDetailRoute}
                  />
                  <PrivateRoute
                    exact
                    path={"/content/lesson/:idHash/:cloneIdHash?"}
                    component={CurriculumDetailRoute}
                  />
                  {selfObject.storeName === "Maintenance" ? (
                    <PrivateRoute
                      exact
                      path={"/members"}
                      component={OnboardingRoute}
                    />
                  ) : null}
                </AnimatedSwitch>
              </HashRouter>
            </MuiThemeProvider>
          </BaseProvider>
        </StyletronProvider>
      </GoogleOAuthProvider>
    );
  }

  return (
    <GoogleOAuthProvider
      clientId={
        process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID
          ? process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID
          : ""
      }
    >
      <StyletronProvider value={engine}>
        <BaseProvider theme={baseTheme}>
          <MuiThemeProvider theme={skaleTheme}>
            <HashRouter>
              {displayedSummitView === "Summit" &&
              (selectedSummit?.id || primaryAuthToken) &&
              !window.location.href.includes("library") ? (
                <NavigationBar />
              ) : null}
              <AnimatedSwitch {...switchConfig} mapStyles={mapStyles}>
                <Route exact path={"/"} component={HomeRoute} />
                <Route exact path={"/start"} component={StartRoute} />
                <Route exact path={"/start/:orgCode"} component={StartRoute} />
                <Route
                  exact
                  path={"/event/rsvp/:inviteUuid"}
                  component={SummitRoute}
                />
                <Route
                  exact
                  path={"/event/:hashedId"}
                  component={SummitRoute}
                />
                <Route
                  exact
                  path={"/library/:sublibrary?"}
                  component={ContentLibraryRoute}
                />
                <PrivateRoute
                  exact
                  path={"/join_organization"}
                  component={JoinOrganizationRoute}
                />
                <PrivateRoute
                  exact
                  path={"/user-dashboard"}
                  component={UserDashboardRoute}
                />
                <PrivateRoute
                  exact
                  path={"/dashboard"}
                  component={DashboardRoute}
                />
                <PrivateRoute
                  exact
                  path={"/dashboard/:category/:idHash"}
                  component={DashboardRoute}
                />
                <PrivateRoute
                  exact
                  path={"/quota-progress"}
                  component={QuotaDashboardRoute}
                />
                <PrivateRoute
                  exact
                  path={"/kudos"}
                  component={KudosModerationRoute}
                />
                <PrivateRoute
                  exact
                  path={"/quota-progress/:category/:idHash"}
                  component={QuotaDashboardRoute}
                />
                <PrivateRoute
                  exact
                  path={"/content"}
                  component={CurriculumRoute}
                />
                <PrivateRoute
                  exact
                  path={"/content/topic/:idHash/:cloneIdHash?"}
                  component={SkillModuleDetailRoute}
                />
                <PrivateRoute
                  exact
                  path={"/content/lesson/:idHash/:cloneIdHash?"}
                  component={CurriculumDetailRoute}
                />
                <PrivateRoute
                  exact
                  path={"/events"}
                  component={SummitAdminListRoute}
                />
                <PrivateRoute
                  exact
                  path={"/events/detail/edit/:idHash/:cloneIdHash?"}
                  component={SummitDetailEditRoute}
                />
                <PrivateRoute
                  exact
                  path={"/events/detail/:id/:cloneIdHash?"}
                  component={SummitAdminRoute}
                />
                <PrivateRoute
                  exact
                  path={"/notifications"}
                  component={AnnouncementsRoute}
                />
                <PrivateRoute
                  exact
                  path={"/members"}
                  component={OnboardingRoute}
                />
                <PrivateRoute
                  exact
                  path={"/performance"}
                  component={PerformanceRoute}
                />
                <PrivateRoute
                  exact
                  path={"/settings"}
                  component={SettingsRoute}
                />
                <Route path={"/share/:idHash"} component={VideoRoute} />
                <Route path={"/video/:idHash"} component={VideoRoute} />
              </AnimatedSwitch>
              <AnnouncementModal />
            </HashRouter>
          </MuiThemeProvider>
        </BaseProvider>
      </StyletronProvider>
    </GoogleOAuthProvider>
  );
}
